import { WalletBalance } from '@/components/billing/WalletBalance'
import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId } from '@/hooks'
import { useAuth, usePrivateChannel } from '@/lib'
import { SettingsNav, SubtleLoader, Tooltip } from '@/ui'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
	ArrowDownTrayIcon,
	EnvelopeIcon,
	EyeIcon,
	InformationCircleIcon,
	PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import {
	broadcastChannel,
	customerWalletShowQueryKey,
	InvoiceListItemData,
	invoicePaymentRenderPdf,
	invoiceRenderPdf,
	issuePaymentWindowSeconds,
	useCustomerValidateBillingDetails,
	useCustomerWalletShow,
	useInvoiceEmail,
	useInvoiceIndex,
	useInvoicePaymentEmail,
	useInvoiceSettingsShow,
	useInvoiceSummary,
	walletEvent,
} from '@jobilla/nexus-sdk'
import { getRouteApi } from '@tanstack/react-router'
import { format, parseISO } from 'date-fns'
import { ChevronRightIcon, Loader2Icon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InvoiceEmailSettingsDialog } from './invoice-email-settings-dialog'
import { InvoicePreviewDialog } from './invoice-preview-dialog'
import { PayInvoiceDialog } from './pay-invoice-dialog'

const invoicesRoute = getRouteApi('/_auth/company/$companyId/settings/invoices')

const POLL_INTERVAL_MS = 10_000
const POLL_DURATION_MS = 60_000

// Ghost icon button (preview / download / spinner) used on each panel row and in the header.
const panelIconButtonClass =
	'inline-flex size-7 items-center justify-center rounded-md text-grey-400 transition-colors hover:bg-brand-50 hover:text-brand-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-grey-400'

export function Invoices() {
	const { t, i18n } = useTranslation('common')
	const { can } = useAuth()
	const organisationId = useJobillaOrgId()

	const wallet = useCustomerWalletShow(organisationId, {
		enabled: !!organisationId,
	})

	// Keep the balance live when the wallet is credited or debited (e.g. paying an invoice).
	usePrivateChannel(
		organisationId && can('billing:read')
			? broadcastChannel.OrganisationWallet({ organisation: organisationId })
			: null,
		{
			[walletEvent.Credited]: [customerWalletShowQueryKey],
			[walletEvent.Debited]: [customerWalletShowQueryKey],
			[walletEvent.Held]: [customerWalletShowQueryKey],
			[walletEvent.Settled]: [customerWalletShowQueryKey],
			[walletEvent.Released]: [customerWalletShowQueryKey],
		},
	)

	const billing = useCustomerValidateBillingDetails(organisationId, undefined, {
		enabled: !!organisationId,
	})

	const invoiceSettings = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId,
	})

	const [emailSettingsOpen, setEmailSettingsOpen] = useState(false)

	// Email settings only apply when invoices are sent through the app, so they
	// are hidden under external invoicing (same gate as the rest of the content).
	const canManageEmailSettings = can('invoice:view')
		&& !!invoiceSettings.data
		&& !invoiceSettings.data.externalInvoicing

	const paymentTerms = billing.data?.customer?.paymentOverview?.paymentTerms ?? null

	const currencyFormatter = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: 'EUR',
	})

	return (
		<SettingsNav active="invoices" fullWidth={false}>
			<section className="space-y-8 mt-8 pr-8 max-w-[800px]">
				<div className="mt-10">
					<div className="flex items-start justify-between gap-4 pb-4">
						<h2 className="text-2xl font-medium tracking-tight">
							{t('settings.invoices.title')}
						</h2>
						<div className="flex items-start gap-6">
							{paymentTerms !== null && (
								<div className="text-center">
									<div className="text-[10px] font-medium text-grey-500 uppercase tracking-wider">
										{t('settings.invoices.paymentTerms.label')}
									</div>
									<div className="text-2xl font-semibold text-grey-900 tabular-nums leading-tight">
										{t('settings.invoices.paymentTerms.value', { count: paymentTerms })}
									</div>
								</div>
							)}
							{paymentTerms !== null && wallet.data && (
								<div className="w-px self-stretch bg-grey-200" aria-hidden="true" />
							)}
							{wallet.data && (
								<div className="text-right">
									<div className="text-[10px] font-medium text-grey-500 uppercase tracking-wider">
										{t('settings.wallet.currentBalance')}
									</div>
									<WalletBalance
										balanceCents={wallet.data.balance}
										taxPercentage={billing.data?.customer?.tax?.taxRate.percentage ?? 0}
										currencyFormatter={currencyFormatter}
										className="text-2xl font-semibold text-grey-900 tabular-nums leading-tight"
									/>
								</div>
							)}
						</div>
					</div>
					{canManageEmailSettings
						? (
							<div className="flex h-px items-center">
								<div className="h-px flex-1 bg-grey-100" />
								<button
									type="button"
									onClick={() => setEmailSettingsOpen(true)}
									className="flex items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer"
								>
									<EnvelopeIcon className="h-3.5 w-3.5" />
									{t('settings.invoices.emails.button')}
								</button>
								<div className="h-px w-5 bg-grey-100" />
							</div>
						)
						: <div className="h-px bg-grey-100" />}
				</div>

				<RequireAccess
					permission="invoice:view"
					fallback={
						<p className="text-sm text-gray-500">
							{t('settings.invoices.noPermission')}
						</p>
					}
				>
					<InvoicesContent />
				</RequireAccess>
			</section>
			<InvoiceEmailSettingsDialog
				open={emailSettingsOpen}
				onClose={() => setEmailSettingsOpen(false)}
			/>
		</SettingsNav>
	)
}

function InvoicesContent() {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const { data, isLoading } = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId,
	})

	if (isLoading || !data) {
		return <SubtleLoader delay={0} />
	}

	if (data.externalInvoicing) {
		return (
			<div className="bg-grey-50 border border-grey-200 rounded-lg p-4">
				<div className="flex items-start gap-3">
					<InformationCircleIcon className="w-5 h-5 text-grey-500 mt-0.5 flex-shrink-0" />
					<div>
						<div className="font-medium text-grey-900 mb-1">
							{t('settings.invoices.externalInvoicing.title')}
						</div>
						<div className="text-sm text-grey-600 space-y-2">
							<p>{t('settings.invoices.externalInvoicing.description')}</p>
							<p>{t('settings.invoices.externalInvoicing.contact')}</p>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return <InvoicesList />
}

type InvoiceFilter = 'open' | 'overdue'

const isHiddenInvoice = (invoice: InvoiceListItemData) => invoice.total === 0

function InvoicesList() {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const [pollUntil, setPollUntil] = useState<number | null>(null)
	const { filter, showHidden } = invoicesRoute.useSearch()
	const navigate = invoicesRoute.useNavigate()

	const isPolling = pollUntil !== null

	useEffect(() => {
		if (pollUntil === null) { return }
		const remaining = pollUntil - Date.now()
		if (remaining <= 0) {
			setPollUntil(null)
			return
		}
		const timer = window.setTimeout(() => setPollUntil(null), remaining)
		return () => window.clearTimeout(timer)
	}, [pollUntil])

	const startPolling = useCallback(() => {
		setPollUntil(Date.now() + POLL_DURATION_MS)
	}, [])

	const { data, isLoading, isFetching: isInvoicesFetching } = useInvoiceIndex(organisationId, {
		enabled: !!organisationId,
		refetchInterval: isPolling ? POLL_INTERVAL_MS : false,
	})

	const summary = useInvoiceSummary(organisationId, {
		enabled: !!organisationId,
		refetchInterval: isPolling ? POLL_INTERVAL_MS : false,
	})

	const isCheckingForUpdates = isPolling && (isInvoicesFetching || summary.isFetching)

	if (isLoading || !data) {
		return <SubtleLoader delay={0} />
	}

	const invoices = data.data

	if (invoices.length === 0) {
		return (
			<div className="border border-grey-100 rounded-lg p-8 text-center">
				<div className="text-sm text-grey-500">
					{t('settings.invoices.emptyState')}
				</div>
			</div>
		)
	}

	const now = Date.now()
	const filteredInvoices = invoices.filter((invoice) => {
		if (isHiddenInvoice(invoice) && !showHidden) { return false }
		if (!filter) { return true }
		if (invoice.status !== 'open') { return false }
		if (filter === 'open') { return true }
		return !!invoice.dueDate && parseISO(invoice.dueDate).getTime() < now
	})

	const setFilter = (next: InvoiceFilter | undefined) =>
		navigate({
			search: (prev) => ({ ...prev, filter: next }),
			replace: true,
		})

	const setShowHidden = (next: boolean) =>
		navigate({
			search: (prev) => ({ ...prev, showHidden: next || undefined }),
			replace: true,
		})

	const formatBucket = (bucket: { amount: number; currency: string } | undefined) =>
		new Intl.NumberFormat(i18n.language, {
			style: 'currency',
			currency: (bucket?.currency ?? 'eur').toUpperCase(),
		}).format((bucket?.amount ?? 0) / 100)

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-2 gap-3">
				<InvoiceStatCard
					label={t('settings.invoices.stats.unpaid', { count: summary.data?.unpaid.count ?? 0 })}
					amount={formatBucket(summary.data?.unpaid)}
				/>
				<InvoiceStatCard
					label={t('settings.invoices.stats.overdue', { count: summary.data?.overdue.count ?? 0 })}
					amount={formatBucket(summary.data?.overdue)}
					tone={(summary.data?.overdue.count ?? 0) > 0 ? 'warning' : 'neutral'}
				/>
			</div>

			<div className="flex items-center justify-between gap-3">
				<p className="text-sm text-gray-500">{t('settings.invoices.description')}</p>
				<div className="flex items-center gap-3">
					{isCheckingForUpdates && (
						<Tooltip content={t('settings.invoices.list.polling')}>
							<Loader2Icon
								className="size-4 text-grey-400 animate-spin"
								aria-label={t('settings.invoices.list.polling')}
							/>
						</Tooltip>
					)}
					<InvoiceFilterChips
						active={filter}
						onChange={setFilter}
						showHidden={!!showHidden}
						onShowHiddenChange={setShowHidden}
					/>
				</div>
			</div>

			<div className="border border-grey-100 rounded-lg overflow-hidden">
				<table className="w-full text-sm">
					<thead className="bg-grey-50 text-left text-xs font-medium text-grey-600 uppercase tracking-wider">
						<tr>
							<th className="px-4 py-2.5">{t('settings.invoices.list.date')}</th>
							<th className="px-4 py-2.5">{t('settings.invoices.list.number')}</th>
							<th className="px-4 py-2.5">{t('settings.invoices.list.status')}</th>
							<th className="px-4 py-2.5 text-right">{t('settings.invoices.list.total')}</th>
							<th className="px-4 py-2.5 text-right">{t('settings.invoices.list.actions')}</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-grey-100">
						{filteredInvoices.length === 0
							? (
								<tr>
									<td colSpan={5} className="px-4 py-8 text-center text-sm text-grey-500">
										{t('settings.invoices.filteredEmptyState')}
									</td>
								</tr>
							)
							: groupByMonth(filteredInvoices, i18n.language).map((entry) =>
								entry.type === 'header'
									? (
										<tr key={`h-${entry.key}`} className="bg-grey-50/60">
											<td
												colSpan={5}
												className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-grey-500"
											>
												{entry.label}
											</td>
										</tr>
									)
									: (
										<InvoiceRow
											key={entry.invoice.id}
											invoice={entry.invoice}
											onPaid={startPolling}
										/>
									)
							)}
					</tbody>
				</table>
			</div>
		</div>
	)
}

function InvoiceRow({ invoice, onPaid }: { invoice: InvoiceListItemData; onPaid: () => void }) {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const [payOpen, setPayOpen] = useState(false)
	const [previewOpen, setPreviewOpen] = useState(false)
	// The receipt being previewed (Jobillan-only); null previews the invoice itself.
	const [previewPaymentId, setPreviewPaymentId] = useState<string | null>(null)
	const [downloadingId, setDownloadingId] = useState<string | null>(null)
	const { can } = useAuth()
	const sendEmailMutation = useInvoiceEmail()
	const sendReceiptEmailMutation = useInvoicePaymentEmail()
	const canSendEmail = can('billing:manage')

	const openPreview = (paymentId: string | null) => {
		setPreviewPaymentId(paymentId)
		setPreviewOpen(true)
	}

	const formatMoney = (amountMinor: number, currency: string) =>
		new Intl.NumberFormat(i18n.language, {
			style: 'currency',
			currency: currency.toUpperCase(),
		}).format(amountMinor / 100)

	const formatDate = (iso: string) => format(parseISO(iso), 'd MMM yyyy')

	// The in-house invoice and receipt PDFs stream from authenticated endpoints, so
	// fetch the bytes (not a URL) and open the resulting blob in a new tab. `id` marks
	// which control is busy so only that one shows a spinner.
	const downloadPdf = useCallback(
		async (id: string, request: () => Promise<{ data: unknown }>) => {
			if (downloadingId) { return }

			setDownloadingId(id)

			try {
				const { data } = await request()
				const objectUrl = URL.createObjectURL(data as Blob)
				window.open(objectUrl, '_blank', 'noopener,noreferrer')
				window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
			} catch {
				// download failed — spinner clears and the user can retry
			} finally {
				setDownloadingId(null)
			}
		},
		[downloadingId],
	)

	const downloadInvoice = () =>
		downloadPdf('invoice', () => invoiceRenderPdf(organisationId, invoice.id, { responseType: 'blob' }))

	const downloadReceipt = (paymentId: string) =>
		downloadPdf(
			paymentId,
			() => invoicePaymentRenderPdf(organisationId, invoice.id, paymentId, { responseType: 'blob' }),
		)

	const paymentMethodLabel = (payment: InvoiceListItemData['payments'][number]) =>
		payment.metadata.last4
			? `${payment.paymentMethod} •••• ${payment.metadata.last4}`
			: payment.paymentMethod

	const issuedAt = parseISO(invoice.issuedAt)
	// Receipts captured as part of issuing the invoice settle within this window
	// of finalisation; sending before it elapses risks omitting one. The window
	// (seconds) comes straight from the backend via the SDK, so there's no magic
	// number to keep in sync here.
	const withinFinalisationWindow = Date.now() - issuedAt.getTime() < issuePaymentWindowSeconds * 1000
	const totalFormatted = formatMoney(invoice.total, invoice.currency)

	// Top-up and inventory-conversion invoices *credit* the wallet when paid, so they
	// can't be settled from it. Every other invoice (campaign, subscription, …) can.
	const isWalletFundingInvoice = invoice.metadata.type === 'wallet_topup'
		|| invoice.metadata.type === 'inventory_conversion'
	// Preview is available to every user, on every invoice.
	const canPreview = true

	// Settled payments (oldest first) each have a downloadable receipt.
	const paidPayments = invoice.payments
	const hasPayments = paidPayments.length > 0
	const canPay = invoice.status === 'open' && !isWalletFundingInvoice && invoice.amountRemaining > 0

	const statusFor = () => {
		if (invoice.status === 'paid' || (hasPayments && invoice.amountRemaining === 0)) {
			return { label: t('settings.invoices.list.statusPaid'), className: 'bg-emerald-50 text-emerald-700' }
		}
		if (invoice.status === 'open' && hasPayments) {
			return { label: t('settings.invoices.list.statusPartlyPaid'), className: 'bg-amber-50 text-amber-700' }
		}
		if (invoice.status === 'open') {
			return { label: t('settings.invoices.list.statusUnpaid'), className: 'bg-amber-50 text-amber-700' }
		}
		return { label: invoice.status, className: 'bg-grey-100 text-grey-600 capitalize' }
	}
	const status = statusFor()

	// Paid invoices headline the final payment date; otherwise the due date.
	const lastPaidAt = paidPayments.at(-1)?.paidAt ?? null
	const dateFor = () => {
		if (invoice.status === 'paid' && lastPaidAt) {
			return { label: t('settings.invoices.list.paidOn'), value: formatDate(lastPaidAt) }
		}
		if (invoice.dueDate) {
			return { label: t('settings.invoices.list.due'), value: formatDate(invoice.dueDate) }
		}
		return { label: t('settings.invoices.list.issued'), value: formatDate(invoice.issuedAt) }
	}
	const headerDate = dateFor()

	return (
		<tr className="text-grey-800">
			<td className="px-4 py-2.5 whitespace-nowrap">{format(issuedAt, 'd MMM yyyy')}</td>
			<td className="px-4 py-2.5 whitespace-nowrap font-medium">
				{invoice.number ?? invoice.stripeInvoiceId}
			</td>
			<td className="px-4 py-2.5">
				<span className="inline-block rounded-full bg-grey-100 px-2 py-0.5 text-xs font-medium capitalize">
					{invoice.status}
				</span>
			</td>
			<td className="px-4 py-2.5 text-right tabular-nums">{totalFormatted}</td>
			<td className="px-4 py-2.5 align-middle">
				<div className="flex items-center justify-end gap-3">
					<Popover>
						{({ close }) => (
							<div className="inline-flex">
								<PopoverButton
									aria-label={t('settings.invoices.list.invoiceDetails')}
									title={t('settings.invoices.list.invoiceDetails')}
									className="group inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-grey-400 transition-colors hover:bg-brand-50 hover:text-brand-600 data-[open]:bg-brand-50 data-[open]:text-brand-600"
								>
									<ChevronRightIcon className="size-4 transition-transform group-data-[open]:rotate-90" />
								</PopoverButton>
								<PopoverPanel
									anchor="bottom end"
									className="z-50 w-[420px] rounded-2xl border border-grey-100 bg-white p-6 text-left shadow-xl [--anchor-gap:8px]"
								>
									<div>
										<div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4">
											<div>
												<div className="text-[11px] font-semibold uppercase tracking-wider text-grey-400">
													{t('settings.invoices.list.invoice')}
												</div>
												<div className="text-sm text-grey-600">
													{invoice.number ?? invoice.stripeInvoiceId}
												</div>
											</div>
											<span
												className={`justify-self-center self-start rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${status.className}`}
											>
												{status.label}
											</span>
											<div className="justify-self-end text-right">
												<div className="text-[11px] font-semibold uppercase tracking-wider text-grey-400">
													{headerDate.label}
												</div>
												<div className="whitespace-nowrap text-sm text-grey-600">
													{headerDate.value}
												</div>
											</div>
										</div>
										<div className="mt-3 flex items-center justify-between gap-4">
											<div className="text-xl font-semibold tracking-tight text-grey-900">
												{totalFormatted}
											</div>
											<div className="flex items-center gap-2">
												{canPreview && (
													<Tooltip
														content={t('settings.invoices.list.previewInvoice')}
														wrapperClassName="inline-flex"
													>
														<button
															type="button"
															onClick={() => openPreview(null)}
															aria-label={t('settings.invoices.list.previewInvoice')}
															className={panelIconButtonClass}
														>
															<EyeIcon className="size-4" />
														</button>
													</Tooltip>
												)}
												<Tooltip
													content={t('settings.invoices.list.download')}
													wrapperClassName="inline-flex"
												>
													<button
														type="button"
														onClick={downloadInvoice}
														disabled={!!downloadingId}
														aria-label={t('settings.invoices.list.download')}
														className={panelIconButtonClass}
													>
														{downloadingId === 'invoice'
															? <Loader2Icon className="size-4 animate-spin" />
															: <ArrowDownTrayIcon className="size-4" />}
													</button>
												</Tooltip>
												{canSendEmail && (
													<Tooltip
														content={withinFinalisationWindow
															? t('settings.invoices.sendEmail.tooEarly')
															: t('settings.invoices.sendEmail.label')}
														wrapperClassName="inline-flex"
													>
														<button
															type="button"
															onClick={() =>
																sendEmailMutation.mutate({
																	organisation: organisationId,
																	invoice: invoice.id,
																})}
															disabled={sendEmailMutation.isPending
																|| withinFinalisationWindow}
															aria-label={t('settings.invoices.sendEmail.label')}
															className={panelIconButtonClass}
														>
															{sendEmailMutation.isPending
																? <Loader2Icon className="size-4 animate-spin" />
																: <PaperAirplaneIcon className="size-4" />}
														</button>
													</Tooltip>
												)}
											</div>
										</div>
									</div>

									{hasPayments && (
										<>
											<div className="my-4 h-px bg-grey-100" />
											<p className="mb-3.5 text-[11px] font-semibold uppercase tracking-wider text-grey-400">
												{t('settings.invoices.list.paidWith')}
											</p>
											<div className="flex flex-col gap-4">
												{paidPayments.map((payment) => (
													<div key={payment.id} className="flex items-center gap-2">
														<div className="min-w-0 flex-1">
															<div className="text-sm font-semibold tracking-tight text-grey-900">
																{payment.receiptNumber}
															</div>
															<div className="mt-0.5 text-[13px] text-grey-400">
																{paymentMethodLabel(payment)}
															</div>
														</div>
														<div className="flex flex-col items-end">
															<div className="text-base font-bold text-grey-900">
																{formatMoney(payment.amount, payment.currency)}
															</div>
															{payment.paidAt
																? (
																	<div className="mt-0.5 text-[13px] text-grey-400">
																		{formatDate(payment.paidAt)}
																	</div>
																)
																: null}
														</div>
														{canPreview && (
															<Tooltip
																content={t('settings.invoices.list.previewReceipt')}
																wrapperClassName="inline-flex"
															>
																<button
																	type="button"
																	onClick={() => openPreview(payment.id)}
																	aria-label={t(
																		'settings.invoices.list.previewReceipt',
																	)}
																	className={panelIconButtonClass}
																>
																	<EyeIcon className="size-4" />
																</button>
															</Tooltip>
														)}
														<Tooltip
															content={t('settings.invoices.list.download')}
															wrapperClassName="inline-flex"
														>
															<button
																type="button"
																onClick={() =>
																	downloadReceipt(payment.id)}
																disabled={!!downloadingId}
																aria-label={`${t('settings.invoices.list.receipt')} ${
																	payment.receiptNumber ?? ''
																}`.trim()}
																className={panelIconButtonClass}
															>
																{downloadingId === payment.id
																	? <Loader2Icon className="size-4 animate-spin" />
																	: <ArrowDownTrayIcon className="size-4" />}
															</button>
														</Tooltip>
														{canSendEmail && (
															<Tooltip
																content={t('settings.invoices.sendReceiptEmail.label')}
																wrapperClassName="inline-flex"
															>
																<button
																	type="button"
																	onClick={() =>
																		sendReceiptEmailMutation.mutate({
																			organisation: organisationId,
																			invoice: invoice.id,
																			payment: payment.id,
																		})}
																	disabled={sendReceiptEmailMutation.isPending
																		&& sendReceiptEmailMutation.variables?.payment
																			=== payment.id}
																	aria-label={t(
																		'settings.invoices.sendReceiptEmail.label',
																	)}
																	className={panelIconButtonClass}
																>
																	{sendReceiptEmailMutation.isPending
																			&& sendReceiptEmailMutation.variables
																					?.payment
																				=== payment.id
																		? (
																			<Loader2Icon className="size-4 animate-spin" />
																		)
																		: <PaperAirplaneIcon className="size-4" />}
																</button>
															</Tooltip>
														)}
													</div>
												))}
											</div>
										</>
									)}

									{canPay && (
										<div className="mt-4 flex items-center justify-between gap-4 border-t border-grey-100 pt-4">
											<div className="flex flex-col gap-0.5">
												<span className="text-[11px] font-semibold uppercase tracking-wider text-grey-400">
													{t('settings.invoices.list.stillToPay')}
												</span>
												<span className="text-lg font-bold text-grey-900">
													{formatMoney(invoice.amountRemaining, invoice.currency)}
												</span>
											</div>
											<button
												type="button"
												onClick={() => {
													close()
													setPayOpen(true)
												}}
												className="cursor-pointer rounded-lg border border-brand-500 bg-white px-4 py-1.5 text-sm font-semibold text-brand-600 transition-colors hover:bg-brand-50"
											>
												{t('settings.invoices.list.pay')}
											</button>
										</div>
									)}
								</PopoverPanel>
							</div>
						)}
					</Popover>
				</div>
			</td>
			{canPay && (
				<PayInvoiceDialog
					open={payOpen}
					onClose={() => setPayOpen(false)}
					onPaid={() => {
						setPayOpen(false)
						onPaid()
					}}
					invoice={invoice}
				/>
			)}
			{canPreview && (
				<InvoicePreviewDialog
					open={previewOpen}
					onClose={() => setPreviewOpen(false)}
					invoice={invoice}
					initialPaymentId={previewPaymentId}
				/>
			)}
		</tr>
	)
}

function InvoiceStatCard(
	{ label, amount, tone = 'neutral' }: { label: string; amount: string; tone?: 'neutral' | 'warning' },
) {
	const toneClasses = tone === 'warning'
		? 'border-amber-200 bg-amber-50'
		: 'border-grey-100 bg-white'

	return (
		<div className={`rounded-lg border px-4 py-3 ${toneClasses}`}>
			<div className="text-xs font-medium text-grey-600 uppercase tracking-wider">
				{label}
			</div>
			<div className="mt-1 text-xl font-semibold text-grey-900 tabular-nums">
				{amount}
			</div>
		</div>
	)
}

function InvoiceFilterChips(
	{ active, onChange, showHidden, onShowHiddenChange }: {
		active: InvoiceFilter | undefined
		onChange: (next: InvoiceFilter | undefined) => void
		showHidden: boolean
		onShowHiddenChange: (next: boolean) => void
	},
) {
	const { t } = useTranslation('common')

	const chips: { value: InvoiceFilter | undefined; label: string }[] = [
		{ value: undefined, label: t('settings.invoices.filterChip.all') },
		{ value: 'open', label: t('settings.invoices.filterChip.unpaid') },
		{ value: 'overdue', label: t('settings.invoices.filterChip.overdue') },
	]

	const chipClass = (isActive: boolean) =>
		`rounded-full border px-3 py-1 text-sm font-medium transition-colors cursor-pointer ${
			isActive
				? 'border-brand-500 bg-brand-50 text-brand-700'
				: 'border-grey-200 bg-white text-grey-700 hover:bg-grey-50'
		}`

	return (
		<div className="flex items-center gap-2">
			<div
				className="flex items-center gap-2"
				role="radiogroup"
				aria-label={t('settings.invoices.filterChip.label')}
			>
				{chips.map((chip) => {
					const isActive = chip.value === active
					return (
						<button
							key={chip.value ?? 'all'}
							type="button"
							role="radio"
							aria-checked={isActive}
							onClick={() => onChange(chip.value)}
							className={chipClass(isActive)}
						>
							{chip.label}
						</button>
					)
				})}
			</div>
			<div className="h-5 w-px bg-grey-200" aria-hidden="true" />
			<button
				type="button"
				aria-pressed={showHidden}
				onClick={() => onShowHiddenChange(!showHidden)}
				className={chipClass(showHidden)}
			>
				{t('settings.invoices.filterChip.showHidden')}
			</button>
		</div>
	)
}

type GroupedEntry =
	| { type: 'header'; key: string; label: string }
	| { type: 'invoice'; invoice: InvoiceListItemData }

function groupByMonth(invoices: InvoiceListItemData[], locale: string): GroupedEntry[] {
	const formatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
	const entries: GroupedEntry[] = []
	let lastKey = ''

	for (const invoice of invoices) {
		const issuedAt = parseISO(invoice.issuedAt)
		const key = `${issuedAt.getFullYear()}-${issuedAt.getMonth()}`

		if (key !== lastKey) {
			entries.push({ type: 'header', key, label: formatter.format(issuedAt) })
			lastKey = key
		}
		entries.push({ type: 'invoice', invoice })
	}

	return entries
}
